import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMemo } from 'react';
import { RxUpdate } from 'react-icons/rx';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import { useCityContext } from '../../../contexts/CityProvider';
import useCitiesActions from '../../../hooks/APIHooks/users/useCitiesActions';
import useProvincesActions from '../../../hooks/APIHooks/users/useProvincesActions';
import { editCitySchema } from '../../../schemas';
import { City, CityReqBody } from '../../../types/cities';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const EditCityModal = ({
  city,
  handleModalClose,
}: {
  city: City;
  handleModalClose: () => void;
}) => {
  // integration of custom API hooks here
  const { getProvinceListHandler } = useProvincesActions('provinceList');
  const { updateCityByIdHandler } = useCitiesActions('');

  // integration of context hooks here
  const { currentUser } = useAuthContext();
  const {
    setFilterProvince,
    setTargetedCity,
    setIsResetFilter,
    setSearchText,
  } = useCityContext();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // formatting dropdown options here
  const provinceOptions = useMemo(() => {
    if (!getProvinceListHandler.data?.is_success) return [];

    return getProvinceListHandler.data.data.map((province) => ({
      id: province.id,
      name: province.province_name,
      code: province.province_code,
    }));
  }, [getProvinceListHandler.data]);

  // formatting dropdown option descriptions here
  const provinceDescriptionOptions = useMemo(() => {
    if (!getProvinceListHandler.data?.is_success) return [];

    return getProvinceListHandler.data.data.map(
      (province) => `(${province.country_name})`
    );
  }, [getProvinceListHandler.data]);

  const editCityHandler = async (values: any, actions: FormikHelpers<any>) => {
    try {
      actions.setSubmitting(true);

      const body: CityReqBody = {
        province: values.province.id,
        city_name: values.city,
        updated_by: currentUser?.username,
      };

      await updateCityByIdHandler
        .mutateAsync({ cityId: city.id, body })
        .then((res) => {
          if (res.is_success) {
            toast.success('City Updated!!');

            handleModalClose();

            setSearchText('');
            setTargetedCity(null);
            setFilterProvince('All');
            setIsResetFilter(true);

            queryClient.invalidateQueries({ queryKey: ['city-list'] });
          } else {
            toast.error(res.message || 'City Updating Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering edit city modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText='update city' />

      <Formik
        initialValues={{
          province: { id: city.province, name: city.province_name },
          city: city.city_name || '',
        }}
        validationSchema={editCitySchema}
        onSubmit={editCityHandler}
      >
        {(props) => (
          <Form className='space-y-4'>
            <AcademiaFormSelect
              name='province'
              label='Select Province'
              options={provinceOptions}
              optionDescription={provinceDescriptionOptions}
              defaultValue={props.values.province}
              showSearchInput
              isRequired
            />

            <AcademiaInputField
              type='text'
              name='city'
              label='City Name'
              placeholder='Enter city name here...'
              isRequired
            />

            <AcademiaButton
              title={
                props.values.province.id === -1
                  ? 'Please, select a province first!'
                  : ''
              }
              type='submit'
              variant='premium'
              icon={<RxUpdate className='size-6' />}
              isLoading={props.isSubmitting}
              isDisabled={props.values.province.id === -1}
              fullWidth
            >
              Update City
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default EditCityModal;
